import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { parse, stringify } from 'qs';
import PropTypes from 'prop-types';

import {
  Tabs,
  Button,
  message,
  Spin,
} from 'antd';
import Header from '../../header';
import ChangePassword from '../components/changePassword';
import UserInformation from '../components/userInformation';
import AddUsers from '../modals/AddUsers';
import { changePassword } from '../../../actions/auth';
import {
  getUserInfo,
  getDocumentCategories,
  changeEmailPreference,
  changeEmailPreferenceForEmailedRecords,
  changeEmailPreferenceForAssignedRecords,
  changeEmailPreferenceForEmrUploadFailures,
  changeEmailPreferenceForFailedFaxes,
  loginAdmin,
  changeModalState,
  getTaskTemplates,
  getTaskTemplatesByQuery,
  deleteTaskTemplate,
  getTaskProviders,
  getTaskProvidersByQuery,
  deleteTaskProvider,
  getActiveFeatures,
  enableFeature,
  disableFeature,
  updateUserDetails,
  sendOtpToSms,
  verifySmsOtp,
} from '../../../actions/userProfile';
import BILLING_ENUMS from '../../billing/constants';
import { addNewUser, clearAddUserModal, openModal } from '../../../actions/modal';
import { getLabelsByPage, deleteLabel, getLabelsByQuery } from '../../../actions/labels';
import { getCategoriesByPage, deleteCategory, getCategoriesByQuery } from '../../../actions/categories';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import { captureAnalytics } from '../../../actions/preload';
import { getTeamNotificationPreferences, updateTeamNotificationPreference } from '../../../actions/teams';
import ManageLabels from './ManageLabels';
import ManageCategories from './ManageCategories';
import ManageTaskTemplates from './ManageTaskTemplates';
import ManageProviders from './ManageProviders';
import ToggleFeatures from './ToggleFeatures';
import ManageTaskRoutingRules from './ManageTaskRoutingRules';
import ManageAutoUploadRules from './ManageAutoUploadRules';
import ManageInsurancePayerMemory from './ManageInsurancePayerMemory';
import ManageAthenaRouting from './ManageAthenaRouting';
import ManageReferralTemplates from './ManageReferralTemplates';
import ManageTeams from './ManageTeams';
import ManageTeamRoutingRules from './ManageTeamRoutingRules';
import NotificationSettings from './NotificationSettings';

import '../../main.scss';
import './settings-reskin.scss';

const { TabPane } = Tabs;

/* global toastr */

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

const tabKeys = {
  user_info: '1',
  notifications: '2',
  manage_labels: '3',
  manage_categories: '4',
  manage_task_templates: '5',
  manage_providers: '6',
  toggle_features: '7',
  change_password: '8',
  manage_task_routing_rules: '9',
  auto_upload_rules: 'auto-upload-rules',
  manage_referral_templates: '10',
  manage_teams: '11',
  manage_team_routing_rules: '12',
  manage_athena_routing: '13',
  insurance_payer_memory: '14',
};

const defaultTabKey = tabKeys.user_info;

class UserProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      displayModal: false,
      autoLabel: false,
      autoTaskRouting: false,
      selfServeAutoUpload: false,
      payerMemory: false,
      selfServeReferralTemplates: false,
      teamsEnabled: false,
      teamNotifications: [],
      athenaRouting: false,
    };
  }

  componentDidMount = async () => {
    // isAdmin (and the rest of userProfile state) gates tabs and the header
    // buttons, so load it on page mount - historically it only loaded when the
    // User Information pane mounted, leaving admin tabs hidden on deep links.
    const [
      ,
      autoLabel,
      autoTaskRouting,
      selfServeAutoUpload,
      selfServeReferralTemplates,
      payerMemory,
      teamsEnabled,
      athenaRouting,
    ] = await Promise.all([
      this.props.handleGetUserInfo(),
      verifyFeatureAccess('auto_labeling'),
      verifyFeatureAccess('auto_task_routing'),
      verifyFeatureAccess('self_serve_auto_upload_rules'),
      verifyFeatureAccess('self_serve_referral_templates'),
      verifyFeatureAccess('insurance_payer_memory'),
      verifyFeatureAccess('teams'),
      verifyFeatureAccess('athena_routing'),
    ]);
    this.props.getActiveFeatures();
    this.setState({
      autoLabel,
      autoTaskRouting,
      selfServeAutoUpload,
      selfServeReferralTemplates,
      payerMemory,
      teamsEnabled,
      athenaRouting,
    });
    // Per-team notification switches only load when the teams feature is on,
    // so flag-off orgs never hit the endpoint and see no change here.
    if (teamsEnabled) this.loadTeamNotifications();
  }

  loadTeamNotifications = async () => {
    const response = await this.props.getTeamNotificationPreferences();
    if (response?.error) return;
    this.setState({ teamNotifications: response?.data?.teams || [] });
  };

  redirectToAdminDashboard = () => {
    const win = window.open(`${window.location.origin}/admin`, '_blank');
    win.opener = null;
  }

  loginAsAdmin = async () => {
    if (!this.props.isAdmin) {
      this.redirectToAdminDashboard();
    }
    const { loginAdmin } = this.props;
    await loginAdmin();

    // TODO (Ammar Khan) use loginErrorMessage
    const { adminLoginSuccess, loginErrorMessage } = this.props;
    if (adminLoginSuccess) {
      const adminUrl = `${window.location.origin}/admin`;
      const win = window.open(adminUrl, '_blank');
      // if popup is blocked, we will just navigate to the admin url in the same tab
      if (!win || win?.closed) {
        window.location = adminUrl;
      } else {
        win.opener = null;
      }
    }
  }

  renderButtons = () => {
    const { isAdmin, changeModalState: changeModal, billingTrialDetails } = this.props;
    const { billing_on_file: billingOnFile } = billingTrialDetails;
    const { UPDATE_PAYMENT_METHOD } = BILLING_ENUMS;
    return (
      <div>
        {
          isAdmin
            && (
              <>
                <Button
                  style={{ marginRight: '10px' }}
                  onClick={() => this.setState({ displayModal: true })}
                >
                  Add Users
                </Button>
                { billingOnFile && (
                  <Button
                    type="primary"
                    onClick={() => changeModal(UPDATE_PAYMENT_METHOD, true)}
                    style={{ marginRight: '10px' }}
                  >
                    Update Payment Details
                  </Button>
                )}
              </>
            )
        }
        <Button type="primary" onClick={() => this.loginAsAdmin()}>
          Admin Login
        </Button>
      </div>
    );
  }

  onCheck = async (e) => {
    this.setState({ loading: true });
    const { checked } = e.target;
    const { changeEmailPreference } = this.props;
    await changeEmailPreference(checked);
    this.setState({ loading: false });
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      toastr.error(notificationErrorMessage, 3);
    } else {
      const toastrMessage = checked
        ? 'You will now receive email notifications when receiving records'
        : 'You will no longer receive email notifications when receiving records';
      message.success({
        content: toastrMessage,
      });
    }
  }

  onCheckForEmail = async (e) => {
    this.setState({ loading: true });
    const { checked } = e.target;
    const { changeEmailPreferenceForEmailedRecords } = this.props;
    await changeEmailPreferenceForEmailedRecords(checked);
    this.setState({ loading: false });
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      toastr.error(notificationErrorMessage, 3);
    } else {
      const toastrMessage = checked
        ? 'You will now receive email notifications when receiving records via email'
        : 'You will no longer receive email notifications when receiving records via email';
      message.success({
        content: toastrMessage,
      });
    }
  }

  onCheckForAssignedEmails = async (e) => {
    this.setState({ loading: true });
    const { checked } = e.target;
    const { changeEmailPreferenceForAssignedRecords } = this.props;
    await changeEmailPreferenceForAssignedRecords(checked);
    this.setState({ loading: false });
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      toastr.error(notificationErrorMessage, 3);
    } else {
      const toastrMessage = checked
        ? 'You will now receive email notifications when being assigned records'
        : 'You will no longer receive email notifications when being assigned records';
      message.success({
        content: toastrMessage,
      });
    }
  }

  onCheckForEmrUploadFailures = async (e) => {
    this.setState({ loading: true });
    const { checked } = e.target;
    const { changeEmailPreferenceForEmrUploadFailures } = this.props;
    await changeEmailPreferenceForEmrUploadFailures(checked);
    this.setState({ loading: false });
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      toastr.error(notificationErrorMessage, 3);
    } else {
      const toastrMessage = checked
        ? 'You will now receive email notifications when EMR uploads fail'
        : 'You will no longer receive email notifications when EMR uploads fail';
      message.success({
        content: toastrMessage,
      });
    }
  }

  onCheckForFailedFaxes = async (e) => {
    this.setState({ loading: true });
    const { checked } = e.target;
    const { changeEmailPreferenceForFailedFaxes } = this.props;
    await changeEmailPreferenceForFailedFaxes(checked);
    this.setState({ loading: false });
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      toastr.error(notificationErrorMessage, 3);
    } else {
      const toastrMessage = checked
        ? 'You will now receive email notifications on total failed faxes sent in a day'
        : 'You will no longer receive email notifications on total failed faxes sent in a day';
      message.success({
        content: toastrMessage,
      });
    }
  }

  // Toggles the caller's own per-team notification opt-out (D5). Reflects the
  // server-confirmed value for the single team, reverting on failure.
  onTeamNotificationChange = async (slug, checked) => {
    this.setState({ loading: true });
    const response = await this.props.updateTeamNotificationPreference(slug, checked);
    this.setState({ loading: false });
    if (response?.error) {
      const errorMessage = response?.response?.data?.error || 'Could not update team notification preference';
      toastr.error(errorMessage, 3);
      return;
    }
    // Reflect the confirmed value from the server for this team only.
    this.setState((prevState) => ({
      teamNotifications: prevState.teamNotifications.map((team) => (
        team.slug === slug
          ? { ...team, receives_notifications: response?.data?.team?.receives_notifications }
          : team
      )),
    }));
    const teamName = response?.data?.team?.name || 'this team';
    const toastrMessage = checked
      ? `You will now receive email notifications for ${teamName}`
      : `You will no longer receive email notifications for ${teamName}`;
    message.success({
      content: toastrMessage,
    });
  }

  onTabChange = (selectedTab) => {
    const { history: { push } } = this.props;
    const tabKey = Object.keys(tabKeys).find((key) => tabKeys[key] === selectedTab);
    if (!tabKey) return;

    push(`/app/user?tab=${tabKey}`);
    captureAnalytics('settings_tab_selected', { tab: tabKey });
  };

  isTabAvailable = (selectedTabKey) => {
    const { isAdmin, provider } = this.props;
    const {
      autoTaskRouting,
      selfServeAutoUpload,
      selfServeReferralTemplates,
      athenaRouting,
      payerMemory,
      teamsEnabled,
    } = this.state;
    const isEmaAutoUpload = localStorage.m8_auto_upload_emr === 'EMA';

    if (selectedTabKey === tabKeys.manage_teams
      || selectedTabKey === tabKeys.manage_team_routing_rules) {
      return isAdmin && teamsEnabled;
    }

    if (selectedTabKey === tabKeys.manage_task_templates) {
      return isEmaAutoUpload;
    }

    if (selectedTabKey === tabKeys.toggle_features) {
      return isAdmin;
    }

    if (selectedTabKey === tabKeys.manage_task_routing_rules) {
      return isAdmin && autoTaskRouting && isEmaAutoUpload;
    }

    if (selectedTabKey === tabKeys.auto_upload_rules) {
      return isAdmin && selfServeAutoUpload;
    }

    if (selectedTabKey === tabKeys.manage_referral_templates) {
      return isAdmin && selfServeReferralTemplates;
    }

    if (selectedTabKey === tabKeys.insurance_payer_memory) {
      return isAdmin && payerMemory;
    }

    if (selectedTabKey === tabKeys.manage_athena_routing) {
      return isAdmin && athenaRouting;
    }

    if (selectedTabKey === tabKeys.change_password) {
      return provider !== 'microsoft_graph_auth';
    }

    return true;
  };

  selectedTabKey = (requestedTab) => {
    const requestedTabKey = tabKeys[requestedTab] || defaultTabKey;
    return this.isTabAvailable(requestedTabKey) ? requestedTabKey : defaultTabKey;
  };

  render() {
    const {
      handleGetUserInfo: getUserInfo,
      passwordChangeStatus,
      passwordChangeMessage,
      addNewUser,
      modalErrorStatus,
      newUser,
      changePassword,
      modalLoading,
      notification_fax_email,
      email_notification_emailed_records,
      email_notification_assigned_records,
      email_notification_emr_upload_failures,
      failedSentFaxesNotification,
      allowedUserDomains,
      provider,
      clearAddUserModal,
      isAdmin,
    } = this.props;

    const {
      loading,
      displayModal,
      autoLabel,
      autoTaskRouting,
      selfServeAutoUpload,
      payerMemory,
      selfServeReferralTemplates,
      teamsEnabled,
      teamNotifications,
      athenaRouting,
    } = this.state;

    let params = {};
    if (location.search) {
      params = parse(location.search, { ignoreQueryPrefix: true });
    }

    const paramTools = {
      params,
      stringify,
      stringifyOptions,
      push: history.push,
    };
    const activeTabKey = this.selectedTabKey(params.tab);

    return (
      <div>
        <Helmet>
          <title>Settings - Medsender</title>
        </Helmet>
        <div className="med-container false-dock">
          <Header
            main="Settings"
            subtext="Change and modify your user settings"
            locationPath={[
              { icon: 'home', text: 'Home', link: '/app' },
              { icon: 'account_circle', text: 'User', link: '/app/user' },
            ]}
          />
          <div className="settings-reskin" style={{ textAlign: 'center' }}>
            {
              displayModal
              && (
                <AddUsers
                  submit={addNewUser}
                  show={() => this.setState({ displayModal: false })}
                  modalLoading={modalLoading}
                  modalErrorStatus={modalErrorStatus}
                  newUser={newUser}
                  clearAddUserModal={clearAddUserModal}
                />
              )
            }
            <Tabs
              // Feature-gated panes (task routing, auto-upload rules) appear only
              // after their async flag checks resolve; remounting the Tabs then lets
              // an uncontrolled defaultActiveKey deep-link land on a gated pane.
              key={`profile-tabs-${isAdmin}-${autoLabel}-${autoTaskRouting}-${selfServeAutoUpload}-${selfServeReferralTemplates}-${teamsEnabled}-${athenaRouting}`}
              style={{ padding: '0px 10px', marginLeft: '20px', marginTop: '10px' }}
              tabPosition="top"
              type="card"
              activeKey={activeTabKey}
              tabBarExtraContent={this.renderButtons()}
              onChange={this.onTabChange}
            >
              <TabPane tab="User Information" key="1">
                <UserInformation
                  submit={this.props.changePassword}
                  changeStatus={this.props.passwordChangeStatus === true}
                  getUserInfo={this.props.getUserInfo}
                  {...this.props}
                />
              </TabPane>
              <TabPane tab="Notifications" key="2">
                <NotificationSettings
                  loading={loading}
                  notificationFaxEmail={notification_fax_email}
                  emailNotificationEmailedRecords={email_notification_emailed_records}
                  emailNotificationAssignedRecords={email_notification_assigned_records}
                  emailNotificationEmrUploadFailures={email_notification_emr_upload_failures}
                  failedSentFaxesNotification={failedSentFaxesNotification}
                  onRecordsReceived={this.onCheck}
                  onRecordsEmailed={this.onCheckForEmail}
                  onAssigned={this.onCheckForAssignedEmails}
                  onEmrUploadFailures={this.onCheckForEmrUploadFailures}
                  onFailedFaxes={this.onCheckForFailedFaxes}
                  teams={teamNotifications}
                  onTeamNotification={this.onTeamNotificationChange}
                />
              </TabPane>
              <TabPane tab="Labels" key="3">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageLabels paramTools={paramTools} {...this.props} isAutoLabelingEnabled={autoLabel} />
                  </div>
                </Spin>
              </TabPane>
              <TabPane tab="Categories" key="4">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageCategories paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              {localStorage.m8_auto_upload_emr === 'EMA'
              && (
              <TabPane tab="Task Templates" key="5">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageTaskTemplates
                      paramTools={paramTools}
                      autoTaskRouting={autoTaskRouting}
                      {...this.props}
                    />
                  </div>
                </Spin>
              </TabPane>
              )}
              <TabPane tab="Providers" key="6">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageProviders paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>

              </TabPane>
              {isAdmin
              && (
              <TabPane tab="Features" key="7">
                <Spin spinning={loading} size="large">
                  <div className="toggle-features">
                    <ToggleFeatures paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>

              </TabPane>
              )}
              {isAdmin
              && autoTaskRouting
              && localStorage.m8_auto_upload_emr === 'EMA'
              && (
              <TabPane tab="Task Routing" key="9">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageTaskRoutingRules paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && selfServeAutoUpload
              && (
              <TabPane tab="Auto-Upload Rules" key="auto-upload-rules">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageAutoUploadRules paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && athenaRouting
              && (
              <TabPane tab="Athena Routing" key={tabKeys.manage_athena_routing}>
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageAthenaRouting paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && selfServeReferralTemplates
              && (
              <TabPane tab="Referral Templates" key="10">
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageReferralTemplates paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && teamsEnabled
              && (
              <TabPane tab="Teams" key={tabKeys.manage_teams}>
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageTeams paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && teamsEnabled
              && (
              <TabPane tab="Team Routing" key={tabKeys.manage_team_routing_rules}>
                <Spin spinning={loading} size="large">
                  <div className="reset-password-container">
                    <ManageTeamRoutingRules paramTools={paramTools} {...this.props} />
                  </div>
                </Spin>
              </TabPane>
              )}
              {isAdmin
              && payerMemory
              && (
              <TabPane tab="Payer Memory" key={tabKeys.insurance_payer_memory}>
                <ManageInsurancePayerMemory />
              </TabPane>
              )}
              {
                provider !== 'microsoft_graph_auth'
                && (
                  <TabPane tab="Change Password" key="8">
                    <div style={{ marginLeft: '20px' }}>
                      <ChangePassword
                        submit={changePassword}
                        changeStatus={passwordChangeStatus === true}
                        passwordChangeMessage={passwordChangeMessage}
                      />
                    </div>
                  </TabPane>
                )
              }
            </Tabs>
          </div>
        </div>
      </div>
    );
  }
}

UserProfile.defaultProps = {
  passwordChangeStatus: false,
  passwordChangeMessage: '',
  jobTitle: '',
  provider: '',
};

UserProfile.propTypes = {
  changePassword: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  handleGetUserInfo: PropTypes.func.isRequired,
  passwordChangeStatus: PropTypes.bool,
  loginAdmin: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  departments: PropTypes.array.isRequired,
  emr: PropTypes.string.isRequired,
  jobTitle: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  associatedFaxNumbers: PropTypes.array.isRequired,
  adminLoginSuccess: PropTypes.bool.isRequired,
  loginErrorMessage: PropTypes.string.isRequired,
  modalLoading: PropTypes.bool.isRequired,
  passwordChangeMessage: PropTypes.string,
  notificationErrorMessage: PropTypes.string.isRequired,
  provider: PropTypes.string,
  allowedUserDomains: PropTypes.array.isRequired,
  openModal: PropTypes.func.isRequired,
  changeModalState: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  billingTrialDetails: PropTypes.object.isRequired,
  getActiveFeatures: PropTypes.func.isRequired,
  changeEmailPreference: PropTypes.func.isRequired,
  changeEmailPreferenceForEmailedRecords: PropTypes.func.isRequired,
  changeEmailPreferenceForAssignedRecords: PropTypes.func.isRequired,
  changeEmailPreferenceForEmrUploadFailures: PropTypes.func.isRequired,
  changeEmailPreferenceForFailedFaxes: PropTypes.func.isRequired,
  getTeamNotificationPreferences: PropTypes.func.isRequired,
  updateTeamNotificationPreference: PropTypes.func.isRequired,
};

export default connect(state => ({
  passwordChangeStatus: state.auth.changePasswordStatus,
  passwordChangeMessage: state.auth.changePasswordMessage,
  email: state.userProfile.email,
  phoneNumber: state.userProfile.phone_number,
  jobTitle: state.userProfile.job_title,
  emr: state.userProfile.emr,
  name: state.userProfile.name,
  adminLoginSuccess: state.userProfile.loginSuccess,
  loginErrorMessage: state.userProfile.loginErrorMessage,
  notificationErrorMessage: state.userProfile.notificationErrorMessage,
  departments: state.userProfile.departments,
  default_fax_numbers: state.userProfile.default_fax_numbers,
  notification_fax_email: state.userProfile.notification_fax_email,
  email_notification_emailed_records: state.userProfile.email_notification_emailed_records,
  email_notification_assigned_records: state.userProfile.email_notification_assigned_records,
  email_notification_emr_upload_failures: state.userProfile.email_notification_emr_upload_failures,
  failedSentFaxesNotification: state.userProfile.failedSentFaxesNotification,
  isAdmin: state.userProfile.isAdmin,
  associatedFaxNumbers: state.userProfile.associated_fax_numbers,
  defaultFaxNumber: state.userProfile.user_fax_number,
  allowedUserDomains: state.userProfile.allowed_user_domains,
  organizationDetails: state.userProfile.organization_details,
  availableEmrs: state.userProfile.available_emrs,
  provider: state.auth.provider,
  modalLoading: state.modal.isLoading,
  modalErrorStatus: state.modal.errorStatus,
  newUser: state.modal.newUser,
  billingTrialDetails: state.auth.billing,
  allOrganizationSubscriptions: state.userProfile.allOrganizationSubscriptions,
}), {
  changePassword,
  getUserInfo,
  getDocumentCategories,
  changeEmailPreference,
  changeEmailPreferenceForEmailedRecords,
  changeEmailPreferenceForAssignedRecords,
  changeEmailPreferenceForEmrUploadFailures,
  changeEmailPreferenceForFailedFaxes,
  loginAdmin,
  handleGetUserInfo: getUserInfo,
  addNewUser,
  clearAddUserModal,
  openModal,
  changeModalState,
  getLabelsByPage,
  deleteLabel,
  deleteCategory,
  deleteTaskTemplate,
  getLabelsByQuery,
  verifyFeatureAccess,
  getCategoriesByPage,
  getCategoriesByQuery,
  getTaskTemplates,
  getTaskTemplatesByQuery,
  getTaskProviders,
  getTaskProvidersByQuery,
  deleteTaskProvider,
  getActiveFeatures,
  enableFeature,
  disableFeature,
  updateUserDetails,
  sendOtpToSms,
  verifySmsOtp,
  getTeamNotificationPreferences,
  updateTeamNotificationPreference,
})(UserProfile);

export {
  UserProfile,
};
